import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Package, MapPin, Tag, Camera, Microphone, QrCode, MagnifyingGlass, Users, Sparkle, Check, ArrowRight } from "@phosphor-icons/react";
import MarketingLayout from "@/components/MarketingLayout";
import SEO from "@/components/SEO";

const FEATURES = [
  {
    Icon: Package, title: "Item cards that feel alive",
    body: "Photo, quantity, tags, notes, location. Edit in place. Nothing locked behind a paywall on the basics.",
    bullets: ["Drag-and-drop ready architecture", "Tags + notes for context", "Quantity tracking for stockpiles"],
  },
  {
    Icon: MapPin, title: "Rooms that nest like real rooms do",
    body: "Garage › Shelf B › Bin 2. Build the tree as deep as your house. Browse by breadcrumb.",
    bullets: ["Unlimited nesting depth", "Reparent locations any time", "Per-location item counts"],
  },
  {
    Icon: Tag, title: "Colored categories",
    body: "Tools, Kitchen, Holiday, Heirlooms — give each category a colour and your library becomes glanceable.",
    bullets: ["7-colour palette to start", "Filter the whole library by category"],
  },
  {
    Icon: Camera, title: "Photos that earn their keep",
    body: "Snap directly from your phone. We store them with end-to-end auth so only you (and your household) can see them.",
    bullets: ["Up to 8 MB per image", "Owner-only access via signed URLs"],
  },
  {
    Icon: Microphone, title: "Voice-first quick add",
    body: "Hands full? Tap the mic, dictate the item, the spot, even a note. Whisper transcribes in seconds.",
    bullets: ["OpenAI Whisper accuracy", "Works in noisy garages"],
  },
  {
    Icon: QrCode, title: "Printable QR labels",
    body: "Generate a sticker. Stick it. Scan it. See what&apos;s inside without unstacking the bins.",
    bullets: ["Public or private per-location", "Print-friendly modal — no third-party software"],
  },
  {
    Icon: MagnifyingGlass, title: "Search you actually trust",
    body: "Name, notes, tags — all searchable. Combine with filters. Voice search if you&apos;re feeling fancy.",
    bullets: ["Instant client-side debounce", "Filters: location + category"],
  },
  {
    Icon: Users, title: "Family households",
    body: "Invite up to 5 people. Everyone sees the same inventory. One subscription, whole house.",
    bullets: ["Email invites with secure tokens", "Tier inherited automatically", "Owner removes/leaves any time"],
  },
];

export default function Features() {
  return (
    <MarketingLayout>
      <SEO
        title="Features"
        description="Photos, voice quick-add, nested locations, QR labels, search, and family sharing — every tool Nestled gives you to keep a calm, organized home."
        path="/features"
      />
      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-10 text-center">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Every feature, all at once</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl text-stoneink-900 font-semibold tracking-tight">
          The whole toolkit for a tidy mind.
        </h1>
        <p className="mt-5 text-stoneink-700 text-lg">Browse what Nestled does — then decide what your home needs first.</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title}
              initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 2) * 0.05 }}
              className="rounded-3xl border border-sand-300 bg-white p-8 shadow-ambient hover:shadow-hover transition-all"
              data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-start gap-5">
                <div className="h-12 w-12 rounded-2xl bg-terracotta-50 text-terracotta-600 grid place-items-center shrink-0">
                  <f.Icon weight="duotone" size={24} />
                </div>
                <div>
                  <h3 className="font-heading text-2xl text-stoneink-900 font-medium tracking-tight">{f.title}</h3>
                  <p className="mt-2 text-stoneink-700 leading-relaxed">{f.body}</p>
                  <ul className="mt-4 space-y-1.5">
                    {f.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-stoneink-700">
                        <Check weight="bold" size={14} className="text-sage-600 mt-1 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16">
        <div className="rounded-3xl bg-stoneink-900 text-white p-10 md:p-14 text-center">
          <Sparkle weight="fill" size={28} className="mx-auto text-terracotta-400" />
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Try them all — free.</h2>
          <p className="mt-3 text-white/85">Start with up to 25 items. Upgrade only if you need unlimited everything.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/register" className="h-12 px-6 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium flex items-center gap-2">
              Get started <ArrowRight weight="bold" size={16} />
            </Link>
            <Link to="/pricing" className="h-12 px-6 rounded-xl bg-white/10 hover:bg-white/15 text-white font-medium flex items-center">See pricing</Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
